import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';

const GuarantorView = () => {
  const { userId } = useParams();
  const [guarantorData, setGuarantorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchGuarantorData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}guarantor/${userId}`);
        setGuarantorData(response.data);
      } catch (error) {
        setError('Error fetching guarantor details');
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuarantorData();
  }, [userId]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toISOString().slice(0, 10);
    } catch {
      return 'Invalid Date';
    }
  };

  const handleDownload = () => {
    const element = contentRef.current;
    const opt = {
      margin: 0.5,
      filename: `Guarantor_Form_${userId}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save();
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (!guarantorData) {
    return <div className="p-6">No guarantor details found</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Guarantor Details</h1>
        <button
          onClick={handleDownload}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </button>
      </div>

      <div
        ref={contentRef}
        className="bg-white shadow-lg rounded-lg p-8 border border-gray-200 relative"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-800">Guarantor Form</h2>
          <p className="text-sm text-gray-500">Essential.ng - Guarantor Details for Applicant</p>
        </div>

        {/* Passport and Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Passport</label>
            {guarantorData.passport ? (
              <img
                src={guarantorData.passport}
                alt="Passport"
                className="w-32 h-32 object-cover rounded border border-gray-300"
                onError={(e) => (e.target.style.display = 'none')}
              />
            ) : (
              <p className="text-gray-500">N/A</p>
            )}
          </div>
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p><strong>Title:</strong> {guarantorData.title || 'N/A'}</p>
              <p><strong>First Name:</strong> {guarantorData.firstName || 'N/A'}</p>
              <p><strong>Previous Name:</strong> {guarantorData.previousName || 'N/A'}</p>
              <p><strong>Residential Address:</strong> {guarantorData.residentialAddress || 'N/A'}</p>
              <p><strong>Time at Address:</strong> {guarantorData.timeAtAddress || 'N/A'}</p>
              <p><strong>Mobile:</strong> {guarantorData.mobile || 'N/A'}</p>
              <p><strong>Email:</strong> {guarantorData.email || 'N/A'}</p>
              <p><strong>Date of Birth:</strong> {formatDate(guarantorData.dateOfBirth)}</p>
              <p><strong>Relationship:</strong> {guarantorData.relationship || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Occupation Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Occupation Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><strong>Current Occupation:</strong> {guarantorData.currentOccupation || 'N/A'}</p>
            <p><strong>Line Manager:</strong> {guarantorData.lineManager || 'N/A'}</p>
            <p><strong>Date Started:</strong> {formatDate(guarantorData.dateStarted)}</p>
            <p><strong>Job Title:</strong> {guarantorData.jobTitle || 'N/A'}</p>
            <p><strong>Previous Occupation:</strong> {guarantorData.previousOccupation || 'N/A'}</p>
          </div>
        </div>

        {/* Oath of Confidentiality */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Oath of Confidentiality</h3>
          <div className="flex items-start">
            <div className="mr-2">
              {guarantorData.oath ? (
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">
              I am acting as a guarantor for: (Full name of the forthcoming employee as stated on employment data form)<br />
              In respect of the forthcoming employee commitments and fidelity. I believe the information provided here to be true and authorize essential.ng:<br />
              - Carry out verification or reference checks from any parties mentioned herein.<br />
              - Use the information obtained with third parties to assess suitability as guarantor and make decision on candidate's employment or confirmation.<br />
              - Handle all information obtained in strictest confidence.<br />
              I understand that if I default on my guarantor's obligation, the information provided in this form may be used released to law enforcement agencies, law court or essential.ng clients to whom the employee is deployed.<br />
              Accept that if the applicant didn't tender two (2) weeks resignation notification letter before leaving the company (quitting), I shall be responsible to pay the company sum of #30,000 being cost for urgent job agency/consultant charge fee regardless of how many weeks he/she has worked /done while expecting salary due and the fact that the company shall take up legal case against me for the amount claims and all legal cost claims.<br />
              This form is true and I can vouch for the character and integrity................. 
            </p>
          </div>
        </div>

        {/* Declaration */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Declaration</h3>
          <div className="flex items-start">
            <div className="mr-2">
              {guarantorData.declaration ? (
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">
              Do not give false information about yourself and your guarantor.<br />
              You are to give two (2) weeks' notice to the management in case of disengagement from service. Failure to give the said notice, one month salary and all entitlement shall be forfeited. If there's no two weeks resignation notification letter tendered, you must pay the company sum of #30,000 being cost for urgent job agency/consultant charge fees which would be paid by you otherwise your guarantor would be liable for the payment.<br />
              You should also note that you may be asked to go to any of Lagos or Ibadan branch at any time temporarily.<br />
              The dress code is white and Black Trouser/Skirt with a Black long tie for men and black shoes.<br />
              Iso notes that lateness attracts #300 each time while 3 times is #500C salaries are paid every 3 of the following month.<br />
              It is a criminal offence to make a false statement on this guarantor's form.<br />
              Kindly fill your account details here for any compensation or staff salary pay off.<br />
              Also if a staff is allegedly caught stealing such staff shall be criminally liable.
            </p>
          </div>
        </div>

        {/* Account Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <p><strong>Account Name:</strong> {guarantorData.accountName || 'N/A'}</p>
            <p><strong>Account Number:</strong> {guarantorData.accountNumber || 'N/A'}</p>
            <p><strong>Bank Name:</strong> {guarantorData.bankName || 'N/A'}</p>
          </div>
        </div>

        {/* Submission Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Submission Details</h3>
          <p><strong>Created At:</strong> {formatDate(guarantorData.createdAt)}</p>
        </div>

        {/* Signature */}
        {guarantorData.signature && (
          <div className="absolute bottom-8 right-8">
            <img
              src={guarantorData.signature}
              alt="Signature"
              className="w-32 h-16 object-contain opacity-80"
              onError={(e) => (e.target.style.display = 'none')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GuarantorView;